import React from 'react'
import type { RouteProps as ReactRouterRouteProps } from 'react-router-dom'
import { Route as ReactRouterRoute } from 'react-router-dom'
import type { FeatureProps } from './Feature/Feature'
import Feature from './Feature/Feature'
import Role from './Role/Role'
import { useAuth } from 'react-auth0'
import ErrorBoundary from './ErrorBoundary/ErrorBoundary'
import type { Permission } from 'paintscout'

export interface RouteProps extends ReactRouterRouteProps {
  auth?: boolean
  features?: FeatureProps['required']
  permission?: Permission
  roles?: string[]
}

/**
 * An ehanced Route component from react-router-dom that allows restrictions on the route
 *
 * note: `auth` must be true for `features`/`permission` props to take effect
 */
export default function Route({ auth, features, permission, roles, ...routeProps }: RouteProps) {
  const { session } = useAuth()

  if (auth && !session) {
    return null
  }

  return (
    <ErrorBoundary>
      <FeatureRoute features={features}>
        <RoleRoute permission={permission} roles={roles}>
          <ReactRouterRoute {...routeProps} />
        </RoleRoute>
      </FeatureRoute>
    </ErrorBoundary>
  )
}

function FeatureRoute({ features, children }: { features?: FeatureProps['required']; children: any }) {
  if (features) {
    return <Feature required={features}>{children}</Feature>
  }

  return children
}

function RoleRoute({ children, permission, roles }: { children: any; permission?: Permission; roles?: string[] }) {
  if (permission || roles) {
    return (
      <Role permission={permission} roles={roles}>
        {children}
      </Role>
    )
  }

  return children
}
